import React, { useEffect, useState } from "react";
import "../../style/player.css";
import axios from "axios";
import SpitialPlayer from "../../components/player/wrapper";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import httpService from "../../services/http.service";
import { getBillingApiRoute, getCMSApiRoute } from "../../utils/api.utils";
import { parseVastXML } from "../../utils/data.utils";
import { getAgeRange } from "../../utils/ageRange.util";
import AccessDeniedScreen from "../../components/common/accessDenied";
import _ from "lodash";
import RootLayout from "../layout";
import { Paper } from "@mui/material";
import { getPlayerDataFromCache } from "../../utils/hooks/usePlayerDataCache";

const getConfigFromURL = async (configURL) => {
  try {
    const response = await fetch(configURL, {});

    const data = await response.json();
    const {
      source: sources,
      thumbnail,
      subTitles,
      subtitles,
      subtitle,
      poster,
    } = data;

    return {
      success: true,
      sources,
      thumbnail,
      poster,
      subTitles: subtitle || subTitles || subtitles,
    };
  } catch (error) {
    console.log("ConfigURL Fetch Error");
    console.log(error);
    return {
      success: false,
    };
  }
};

const isCinemaTicketPurchased = async (contentSlug, slug) => {
  try {
    const response = await httpService.get(
      getBillingApiRoute(
        "payment?" +
        new URLSearchParams({
          pageNumber: 1,
          pageIndex: 40,
          orderType: "cinema_ticket",
          status: 1,
          ticketKey: decodeURI(slug),
        })
      )
    );

    const { data } = response.data;

    if (response.status !== 200) return response.status;

    if (data?.results?.length !== 0) {
      const _data = await fetchCinemaPlayerResource(
        contentSlug,
        data?.results[0]?.traceCode
      );

      return _data;
    }
  } catch (error) {
    console.log(`SSR Error on CinemaTicket Get: ${error}`);
    return {};
  }
};

const fetchCinemaPlayerResource = async (slug, ticketTraceCode) => {
  try {
    const response = await httpService.get(
      getCMSApiRoute(
        "content/player-resource-cinema?" +
        new URLSearchParams({
          slug,
          ticketTraceCode,
        })
      )
    );

    const { data } = response.data;
    if (response.status !== 200) return response.status;

    return data;
  } catch (error) {
    console.log(`SSR Error on PlayerResource Get: ${error}`);
    return {};
  }
};

const getWatchTrackerData = async (contentKey, isEpisode, season, episode) => {
  try {
    const response = await httpService.get(
      getCMSApiRoute(
        "action/watch-tracker?" +
        new URLSearchParams(
          _.omitBy(
            {
              contentKey,
              isEpisode,
              season,
              episode,
            },
            _.isNil
          )
        ).toString()
      )
    );

    const { data } = response.data;

    // API returns { results: [...] } structure
    return data?.results?.[0] || data?.[0] || {};
  } catch (error) {
    console.log(`Error on SSR Watch Tracker get`);
    console.log(error);
    return {};
  }
};

async function getData(params, searchParams) {
  try {
    const { isTicketing } = searchParams;
    const decodedUrl = decodeURIComponent(params.slug);

    if (isTicketing && isTicketing == 1) {
      return await isCinemaTicketPurchased(
        params.slug,
        decodeURIComponent(searchParams.contentSlug)
      );
    } else {
      const response = await httpService.get(
        getCMSApiRoute(
          "content/player-resource?" +
          new URLSearchParams({
            slug: decodedUrl,
          })
        )
      );

      const { data } = response.data;

      if (response.status !== 200) return response.status;

      return data;
    }
  } catch (error) {
    console.log(error);
    return {};
  }
}

async function getMoviesData(params, searchParams) {
  try {
    const data = await getData(params, searchParams);

    if (data === 400)
      return {
        rawData: 400,
      };

    if (!data) {
      return {
        movie: "",
        vast: "",
        hasErr: true,
        message: "محتوا با مشخصات مورد نظر یافت نشد",
      };
    }

    if (
      data?.contentType?.kind === "series" ||
      data?.contentType?.kind === "program"
    ) {
      const behavior = data?.behavior;
      const { s, e } = searchParams;

      if (!s || !e)
        return {
          movie: "",
          vast: "",
          hasErr: true,
          message: "اطلاعات محتوای مشخص شده نامعتبر می باشد",
        };

      const episodeItem = data?.contentType.seriesInfo.find(
        (x) => +x.episodeNumber === +e && +x.seasonNumber === +s
      );

      if (!episodeItem)
        return {
          movie: "",
          vast: "",
          hasErr: true,
          message: "قسمت مورد نظر یافت نشد",
        };

      const watchTrackData = await getWatchTrackerData(params.slug, 1, +s, +e);
      const posters = episodeItem?.data?.meta?.contentPhotos?.filter(
        (x) => x.type === "videoPlayerCover"
      );
      const hotpoints = episodeItem?.data?.meta?.hotpoints;
      const episodeSelfAds = episodeItem?.data?.meta?.episodeSelfAds;
      let { sources, thumbnail, subTitles, configURL } = episodeItem.data;

      if (!sources && !configURL) {
        return {
          movie: "",
          vast: "",
          hasErr: true,
          message: "EPISODE_SOURCE_NOT_FOUND",
        };
      }

      if (configURL) {
        const confData = await getConfigFromURL(configURL);
        if (!confData.success) {
          return {
            movie: "",
            vast: "",
            hasErr: true,
            message: "منبعی برای پخش تعریف نشده",
          };
        } else {
          sources = confData.sources;
          thumbnail = confData.thumbnail;
          subTitles = confData.subTitles;
        }
      }

      const { ads } = data;

      let vast;

      if (episodeSelfAds) {
        const vastDoc = await axios.get(episodeSelfAds, {
          validateStatus: false,
        });

        if (vastDoc.status === 200) {
          try {
            await JSON.parse(vastDoc.data);
          } catch (error) {
            const doc = vastDoc.data;
            vast = parseVastXML(doc.toString());
          }
        }
      } else if (ads && ads.src) {
        const vastDoc = await axios.get(ads.src, {
          validateStatus: false,
        });

        if (vastDoc.status === 200) {
          try {
            await JSON.parse(vastDoc.data);
          } catch (error) {
            const doc = vastDoc.data;
            vast = parseVastXML(doc.toString());
          }
        }
      }

      return {
        movie: { sources, thumbnail, subTitles },
        vast,
        hotPoints: hotpoints || [],
        ads,
        hasErr: false,
        posters,
        behavior,
        rawData: data,
        watchTrackData,
      };
    } else {
      const hotpoints = data?.meta?.hotpoints;
      let {
        sources,
        ads,
        thumbnail,
        subTitles,
        configURL,
        isTicketing,
        isLive,
        behavior,
      } = data;

      if (configURL) {
        const confData = await getConfigFromURL(configURL);

        if (!confData.success) {
          return {
            movie: "",
            vast: "",
            hasErr: true,
            message: "منبعی برای پخش تعریف نشده",
          };
        } else {
          sources = confData.sources;
          thumbnail = confData.thumbnail;
          subTitles = confData.subTitles;
        }
      }

      const watchTrackData = await getWatchTrackerData(params.slug);
      const posters = data?.contentPhotos?.filter(
        (x) => x.type === "videoPlayerCover"
      );
      let vast;

      if (ads && ads.src) {
        const vastDoc = await axios.get(ads.src, {
          validateStatus: false,
        });
        if (vastDoc.status === 200) {
          try {
            await JSON.parse(vastDoc.data);
          } catch (error) {
            const doc = vastDoc.data;
            vast = parseVastXML(doc.toString());
          }
        }
      }

      return {
        movie: { sources, thumbnail, subTitles },
        vast,
        ads,
        posters,
        hotPoints: hotpoints || [],
        hasErr: false,
        isLive,
        isTicketing,
        behavior,
        rawData: data,
        watchTrackData,
      };
    }
  } catch (error) {
    console.log(error);
    console.log(`Error on Player SSR Init: ${error}`);
    return {
      message: error.message,
      hasErr: true,
    };
  }
}

const checkAuth = () => localStorage.getItem("webTvUserToken");

const MoviePlayer = () => {
  const auth = checkAuth();
  const navigate = useNavigate();
  const params = useParams();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);

  if (!auth) {
    navigate(`/login?ref=${encodeURIComponent(window.location.pathname)}`);
  }

  useEffect(() => {
    setLoading(true);
    getMoviesData(params, params).then((data) => {
      setData(data);
      setLoading(false);
    });
  }, [params]);

  if (loading)
    return (
      <main className="w-screen h-screen flex justify-center items-center bg-black">
        <img src="/three-dots.svg" alt="loading..." />
      </main>
    );

  if (data && data.rawData) {
    const token = localStorage.getItem("webTvUserToken");
    const ageRange = getAgeRange(token);

    if (!ageRange || data.rawData.ageRange > ageRange || data.rawData === 400) {
      return <AccessDeniedScreen />;
    } else {
      return (
        <RootLayout>
          <main className="w-full" dir="ltr">
            <SpitialPlayer data={data} />
          </main>
        </RootLayout>
      );
    }
  } else {
    return (
      <main className="w-full h-screen flex items-start md:items-center justify-center bg-black dark">
        <Paper className="p-5">
          {
            "خطا در دریافت اطلاعات محتوا، لطفا اتصال اینترنت خود را بررسی کرده و سپس مجددا تلاش فرمائید"
          }
        </Paper>
      </main>
    );
  }
};

export default MoviePlayer;
